<script setup lang="ts" generic="TValue extends string | number = string | number">
/**
 * UI-kit component
 * Category: Selects
 * Name: 'AppMultiSelectSearchable'
 */
import type { AppMultiSelectSearchableProps } from '../types/interfaces'
import type { AppSelectOption } from '@brighterly/lib-core-types/ui/selects/interfaces'

import { faXmark } from '@fortawesome/pro-regular-svg-icons'

import { AppText } from '@shared/ui/typography'
import { AppIcon } from '@shared/ui/icons'
import {
  AppDropdown,
  AppDropdownItem,
  AppDropdownLoader,
} from '@shared/ui/dropdowns'
import { BtnSecondary } from '@shared/ui/buttons'

import { useAppSelectClasses } from '../../app-select'
import { useAppMultiSelectSearchable } from '../composables/use-app-multi-select-searchable.hook'

defineOptions({
  name: 'AppMultiSelectSearchable',
})

const props = withDefaults(defineProps<AppMultiSelectSearchableProps<TValue>>(), {
  placeholder: '',
  label: undefined,
  optionalText: undefined,
  required: false,
  state: 'static',
  disabled: false,
  message: undefined,
  prependIcon: undefined,
  panelMinWidth: undefined,
  clearable: false,
  // search
  selectedOptions: undefined,
  noResultsText: 'No results',
  loading: false,
  // pagination
  infinite: false,
  hasMore: false,
})

/**
 * `@search` debounce + reset semantics are identical to AppSelectSearchable.
 * `@load-more` carries the current query (or '' in browse mode).
 */
const emit = defineEmits<{
  open: []
  close: []
  search: [query: string]
  'load-more': [query: string]
}>()

// `update:modelValue` is auto-emitted by `defineModel`.
const modelValue = defineModel<TValue[]>()

defineSlots<{
  'prepend-icon'?: () => unknown
  option?: (scope: {
    option: AppSelectOption<TValue>
    selected: boolean
    disabled: boolean
  }) => unknown
}>()

const {
  triggerRef,
  inputRef,
  panelRef,
  isOpen,
  panelId,
  labelId,
  floatingStyles,
  isDisabled,
  isError,
  isSuccess,
  isHighlighted,
  displayedOptions,
  inputValue,
  inputPlaceholder,
  isOptionSelected,
  isAllSelected,
  isSelectAllDisabled,
  toggleSelectAll,
  showClearButton,
  showNoResults,
  isClearDisabled,
  isLoadMoreDisabled,
  showClearValueButton,
  focusInput,
  handleChevronClick,
  handleClearValueClick,
  handleInputFocus,
  handleInputBlur,
  handleClearClick,
  handleLoadMoreClick,
  handleTriggerKeydown,
  handleSelect,
} = useAppMultiSelectSearchable<TValue>({
  modelValue,
  props,
  onOpen: () => emit('open'),
  onClose: () => emit('close'),
  onSearch: (query) => emit('search', query),
  onLoadMore: (query) => emit('load-more', query),
})

// Default-skin visuals — view layer, not the headless core.
const { rootClasses, triggerClasses, messageColor, chevronIcon } = useAppSelectClasses({
  isOpen,
  isDisabled,
  isError,
  isSuccess,
})
</script>

<template>
  <div :class="rootClasses">
    <div
      v-if="label"
      class="app-select__label-row"
    >
      <AppText
        :id="labelId"
        name="body2"
        color="slate-900"
        class="app-select__label"
      >
        {{ label }}<span
          v-if="required"
          class="app-select__required"
        >*</span>
      </AppText>
      <AppText
        v-if="optionalText"
        name="body2"
        color="slate-400"
        class="app-select__optional"
      >
        {{ optionalText }}
      </AppText>
    </div>

    <div
      ref="triggerRef"
      :class="triggerClasses"
      role="combobox"
      :aria-expanded="isOpen"
      aria-haspopup="listbox"
      :aria-controls="panelId"
      @click="focusInput"
    >
      <span
        v-if="$slots['prepend-icon'] || prependIcon"
        class="app-select__prepend"
      >
        <slot name="prepend-icon">
          <AppIcon
            v-if="prependIcon"
            :icon="prependIcon"
          />
        </slot>
      </span>

      <input
        ref="inputRef"
        v-model="inputValue"
        type="text"
        class="app-select__input"
        :placeholder="inputPlaceholder"
        :disabled="isDisabled"
        :aria-labelledby="label ? labelId : undefined"
        :aria-required="required || undefined"
        :aria-invalid="isError || undefined"
        @focus="handleInputFocus"
        @blur="handleInputBlur"
        @keydown="handleTriggerKeydown"
      >

      <button
        v-if="showClearButton"
        type="button"
        class="app-select__clear-btn"
        :disabled="isClearDisabled"
        tabindex="-1"
        aria-label="Clear search"
        @mousedown.prevent
        @click.stop="handleClearClick"
      >
        <AppIcon :icon="faXmark" />
      </button>
      <button
        v-else-if="showClearValueButton"
        type="button"
        class="app-select__clear-btn"
        :disabled="isDisabled"
        tabindex="-1"
        aria-label="Clear selection"
        @mousedown.prevent
        @click.stop="handleClearValueClick"
      >
        <AppIcon :icon="faXmark" />
      </button>
      <button
        v-else
        type="button"
        class="app-select__chevron-btn"
        :disabled="isDisabled"
        tabindex="-1"
        aria-label="Toggle dropdown"
        @mousedown.prevent
        @click.stop="handleChevronClick"
      >
        <AppIcon :icon="chevronIcon" />
      </button>
    </div>

    <AppText
      v-if="message"
      name="body2"
      :color="messageColor"
      class="app-select__message"
    >
      {{ message }}
    </AppText>

    <Teleport to="body">
      <div
        v-if="isOpen"
        :id="panelId"
        ref="panelRef"
        role="listbox"
        aria-multiselectable="true"
        class="app-select__panel"
        :style="floatingStyles"
      >
        <AppDropdown>
          <AppDropdownLoader v-if="loading && displayedOptions.length === 0" />

          <template v-else>
            <div
              v-if="displayedOptions.length > 0"
              :class="[
                'app-select__option',
                'app-select__option--select-all',
                {
                  'app-select__option--selected': isAllSelected,
                  'app-select__option--disabled': isSelectAllDisabled,
                },
              ]"
              role="option"
              :aria-selected="isAllSelected"
              :aria-disabled="isSelectAllDisabled || undefined"
              tabindex="-1"
              @click.stop="toggleSelectAll"
            >
              <AppDropdownItem
                tag="div"
                text="All"
                :selected="isAllSelected"
                :disabled="isSelectAllDisabled"
                :checkbox="true"
                tabindex="-1"
              />
            </div>

            <div
              v-for="(option, index) in displayedOptions"
              :key="String(option.value)"
              :data-app-select-option="index"
              :class="[
                'app-select__option',
                {
                  'app-select__option--highlighted': isHighlighted(index),
                  'app-select__option--selected': isOptionSelected(option as AppSelectOption<TValue>),
                  'app-select__option--disabled': option.disabled === true,
                },
              ]"
              role="option"
              :aria-selected="isOptionSelected(option as AppSelectOption<TValue>)"
              :aria-disabled="option.disabled || undefined"
              tabindex="-1"
              @click="handleSelect(option as AppSelectOption<TValue>)"
            >
              <slot
                name="option"
                :option="option"
                :selected="isOptionSelected(option as AppSelectOption<TValue>)"
                :disabled="option.disabled === true"
              >
                <AppDropdownItem
                  tag="div"
                  :text="option.text"
                  :selected="isOptionSelected(option as AppSelectOption<TValue>)"
                  :disabled="option.disabled"
                  :checkbox="true"
                  tabindex="-1"
                />
              </slot>
            </div>

            <div
              v-if="showNoResults"
              class="app-select__no-results"
            >
              <AppText
                name="body1"
                color="slate-500"
              >
                {{ noResultsText }}
              </AppText>
            </div>
          </template>

          <template
            v-if="infinite && hasMore"
            #footer
          >
            <div class="app-select__footer">
              <AppDropdownLoader v-if="loading" />
              <BtnSecondary
                v-else
                text="Load more"
                :disabled="isLoadMoreDisabled"
                @click="handleLoadMoreClick"
              />
            </div>
          </template>
        </AppDropdown>
      </div>
    </Teleport>
  </div>
</template>

<style scoped lang="scss" src="../styles/_app-multi-select-searchable.scss" />
